import { paymentApiService } from '@/api/payment-api.service';
import { addItemToQueriesData } from '@/utils/update-query-data';
import type {
  BlockAmountDto,
  ChargeAmountDto,
  ChargeAmountResponseDto,
  CreditWithRefundDto,
  CreditWithRefundResponseDto,
  Payment,
  PromoteStoredPaymentMethodDto,
  StoreIbanPaymentMethodDto,
  StorePaymentMethodDto,
} from '@carvia/ros-client-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { sumBy } from 'lodash';
import { createToast } from 'mosha-vue-toastify';
import { toValue, type MaybeRef } from 'vue';
import { PAYMENTS_QUERY_KEY } from './use-payments';
import { BOOKINGS_QUERY_KEY } from './use-bookings';
import { INVOICES_QUERY_KEY } from './use-invoices';

export const useChargeAmount = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['charge-amount'],
    mutationFn: (data: ChargeAmountDto) => paymentApiService.chargeAmount(data),
    onSuccess: (result, request) => {
      showChargingStatusToast(result);
      queryClient.invalidateQueries({
        queryKey: [BOOKINGS_QUERY_KEY, request.bookingId],
      });
      queryClient.invalidateQueries({
        queryKey: [INVOICES_QUERY_KEY],
      });
      if (result.paymentLink) {
        queryClient.invalidateQueries({
          queryKey: [PAYMENTS_QUERY_KEY],
        });
      }
    },
  });
};

export const useCreditWithRefund = ({ locale }: { locale: MaybeRef<string> }) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['credit-with-refund'],
    mutationFn: (data: CreditWithRefundDto) => paymentApiService.creditWithRefund(data),
    onSuccess: (result, request) => {
      showCreditStatusToast(result, request, toValue(locale));
      for (const payment of result.payments) {
        addItemToQueriesData<Payment>([PAYMENTS_QUERY_KEY], payment, queryClient);
      }
    },
  });
};

export const useBlockAmount = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['block-amount'],
    mutationFn: (data: BlockAmountDto) => paymentApiService.blockAmount(data),
    onSuccess: (result, request) => {
      showChargingStatusToast(result);
      queryClient.invalidateQueries({
        queryKey: [BOOKINGS_QUERY_KEY, request.bookingId],
      });
    },
  });
};

export const useStorePaymentMethod = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['store-payment-method'],
    mutationFn: (data: StorePaymentMethodDto) => paymentApiService.storePaymentMethod(data),
    onSuccess: (result, request) => {
      showChargingStatusToast(result);
      queryClient.invalidateQueries({
        queryKey: [BOOKINGS_QUERY_KEY, request.bookingId],
      });
      queryClient.invalidateQueries({
        queryKey: ['stored-payment-methods', request.bookingId],
      });
    },
  });
};

export const useStoreIbanPaymentMethod = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['store-iban-payment-method'],
    mutationFn: (data: StoreIbanPaymentMethodDto) => paymentApiService.storeIbanPaymentMethod(data),
    onSuccess: (result, request) => {
      queryClient.setQueryData(['stored-payment-methods', request.bookingId], result);
      queryClient.invalidateQueries({
        queryKey: [BOOKINGS_QUERY_KEY, request.bookingId],
      });
    },
  });
};

export const usePromoteStoredPaymentMethodToPeriodicCharge = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['promote-stored-payment-method-periodic-charge'],
    mutationFn: (data: PromoteStoredPaymentMethodDto) =>
      paymentApiService.promoteStoredPaymentMethodToPeriodicCharge(data),
    onSuccess: (result, request) => {
      queryClient.setQueryData(['stored-payment-methods', request.bookingId], result);
      queryClient.invalidateQueries({
        queryKey: [BOOKINGS_QUERY_KEY, request.bookingId],
      });
    },
  });
};

export const useStoredPaymentMethods = (bookingId: MaybeRef<string>) => {
  return useQuery({
    queryKey: ['stored-payment-methods', bookingId],
    queryFn: () => paymentApiService.getStoredPaymentMethods(toValue(bookingId)),
  });
};

const showChargingStatusToast = (result: ChargeAmountResponseDto) => {
  if (result.charged) {
    createToast('Charged Amount', {
      position: 'bottom-right',
      type: 'success',
    });
  } else if (result.paymentLink) {
    createToast('Created Payment Link', {
      position: 'bottom-right',
      type: 'success',
    });
  } else {
    createToast('Could not charge the amount.', {
      position: 'bottom-right',
      type: 'warning',
    });
  }
};

const showCreditStatusToast = (
  result: CreditWithRefundResponseDto,
  request: CreditWithRefundDto,
  locale: string,
) => {
  const dueAmount = sumBy(request.ledgerEntries, 'amount') - result.creditedAmount;
  if (!dueAmount) {
    createToast('Refunded Amount', {
      position: 'bottom-right',
      type: 'success',
    });
  } else {
    createToast(
      `Could not refund the full amount. Still due: ${dueAmount.toLocaleString(locale)} €`,
      {
        position: 'bottom-right',
        type: 'warning',
      },
    );
  }
};
